// A small Markdown subset rendered straight to React elements. No string ever
// reaches the DOM as HTML, so raw HTML in the source shows up as text.
// Blocks: headings, paragraphs, lists, quotes, rules, fenced code.
// Inline: **bold**, *italic*, `code`, [links](url), bare urls.

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import './Markdown.scss';

// Browsers read `/\host` as `//host`, so a backslash is refused as well
const SAFE_URL = /^(https?:\/\/|mailto:|tel:|\/(?![/\\]))/i;

const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*|__[^_]+__)|(\*[^*\s][^*]*\*|_[^_\s][^_]*_)|(\[[^\]]+\]\([^)\s]+\))|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

function link(href, children, key) {
  if (!SAFE_URL.test(href)) return <Fragment key={key}>{children}</Fragment>;
  if (href.startsWith('/'))
    return (
      <Link key={key} to={href}>
        {children}
      </Link>
    );
  // A new tab only for a page: mailto: and tel: would leave an empty one
  const isPage = /^https?:/i.test(href);
  return (
    <a
      key={key}
      href={href}
      {...(isPage && { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  );
}

function inline(text, keyPrefix = '') {
  const out = [];
  let last = 0;
  let match;
  const re = new RegExp(INLINE.source, 'g');
  while ((match = re.exec(text))) {
    const [token, code, bold, italic, mdLink, url] = match;
    const end = match.index + token.length;
    // Underscores inside a word (snake_case, emails) stay text, like CommonMark
    if (
      token[0] === '_' &&
      /\w/.test((text[match.index - 1] ?? '') + (text[end] ?? ''))
    ) {
      re.lastIndex = match.index + 1;
      continue;
    }
    if (match.index > last) out.push(text.slice(last, match.index));
    const key = `${keyPrefix}${match.index}`;
    if (code) out.push(<code key={key}>{code.slice(1, -1)}</code>);
    else if (bold)
      out.push(<strong key={key}>{inline(bold.slice(2, -2), key)}</strong>);
    else if (italic)
      out.push(<em key={key}>{inline(italic.slice(1, -1), key)}</em>);
    else if (mdLink) {
      const [, label, href] = mdLink.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      out.push(link(href, inline(label, key), key));
    } else if (url) out.push(link(url, url, key));
    last = end;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Lines inside a paragraph keep their breaks, the way organizers type them
function lines(list, key) {
  return list.map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {inline(line, `${key}-${i}-`)}
    </Fragment>
  ));
}

const BULLET = /^\s*[-*+]\s+/;
const NUMBER = /^\s*\d+[.)]\s+/;
const FENCE = /^\s*```/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const QUOTE = /^\s*>/;

const startsBlock = (line) =>
  /^#{1,6}\s/.test(line) ||
  [FENCE, RULE, QUOTE, BULLET, NUMBER].some((re) => re.test(line));

function parse(source) {
  const src = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < src.length) {
    const line = src[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    if (FENCE.test(line)) {
      const body = [];
      i++;
      while (i < src.length && !FENCE.test(src[i])) body.push(src[i++]);
      i++;
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }
    // A closing run of #s needs a space before it, so `# C#` keeps its #
    const heading = line.match(/^(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/);
    if (heading) {
      blocks.push({ type: 'h', level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const body = [];
      while (i < src.length && QUOTE.test(src[i]))
        body.push(src[i++].replace(/^\s*>\s?/, ''));
      blocks.push({ type: 'quote', lines: body });
      continue;
    }
    const listRe = BULLET.test(line)
      ? BULLET
      : NUMBER.test(line)
        ? NUMBER
        : null;
    if (listRe) {
      const items = [];
      while (i < src.length && src[i].trim()) {
        if (listRe.test(src[i])) items.push([src[i].replace(listRe, '')]);
        else items.at(-1).push(src[i].trim());
        i++;
      }
      // Items split by blank lines are separate lists, so each keeps its number
      const start = listRe === NUMBER ? parseInt(line, 10) : undefined;
      blocks.push({ type: listRe === BULLET ? 'ul' : 'ol', start, items });
      continue;
    }
    // The first line is taken whatever it is: one no block above claims (a `#`
    // line with U+2028 in it) would otherwise be retried forever
    const body = [src[i++]];
    while (i < src.length && src[i].trim() && !startsBlock(src[i]))
      body.push(src[i++]);
    blocks.push({ type: 'p', lines: body });
  }
  return blocks;
}

export default function Markdown({ text, className = '' }) {
  const blocks = parse(text ?? '');
  return (
    <div className={'markdown ' + className}>
      {blocks.map((block, k) => {
        switch (block.type) {
          case 'h': {
            // h1 belongs to the page, so the scale starts one level down
            const Tag = `h${Math.min(block.level + 1, 6)}`;
            return <Tag key={k}>{inline(block.text, `${k}-`)}</Tag>;
          }
          case 'hr':
            return <hr key={k} />;
          case 'code':
            return (
              <pre key={k}>
                <code>{block.text}</code>
              </pre>
            );
          case 'quote':
            return <blockquote key={k}>{lines(block.lines, k)}</blockquote>;
          case 'ul':
          case 'ol': {
            const Tag = block.type;
            return (
              <Tag key={k} start={block.start}>
                {block.items.map((item, j) => (
                  <li key={j}>{lines(item, `${k}-${j}`)}</li>
                ))}
              </Tag>
            );
          }
          default:
            return <p key={k}>{lines(block.lines, k)}</p>;
        }
      })}
    </div>
  );
}
